import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectRuntimeConfigQueryKey } from "@/lib/queries.ts"
import { extractRpcErrorMessage } from "@/lib/rpcError.ts"

import type { ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("ProjectRootFolderViewModel")

export class ProjectRootFolderViewModel extends Model {
  readonly isReadonly = observable.box(true)
  readonly value = observable.box<string>("")
  readonly initialValue = observable.box<string>("")
  readonly isSaving = observable.box<boolean>(false)
  readonly error = observable.box<string | null>(null)

  readonly projectConfigured = observable.box(true)

  readonly hasChanges = computed(() => this.value.get() !== this.initialValue.get())

  constructor(
    readonly context: OrgContext,
    readonly canEditProject: IObservableValue<boolean>,
    private readonly projectKey: ProjectKey,
    private readonly repository: string
  ) {
    super("ProjectRootFolderViewModel")

    void this.load()
  }

  onSave = async () => {
    await this.save()
    toast.success("Root folder saved")
  }

  async load() {
    this.isLoading.set(true)
    this.error.set(null)
    this.isReadonly.set(true)

    try {
      const cfg = await withGlobalLoader(this.context, this.context.queries.getProjectRuntimeConfig(this.projectKey, this.repository))
      runInAction(() => {
        const val = cfg?.first?.rootFolder ?? ""
        this.projectConfigured.set(!!cfg)
        this.isReadonly.set(!cfg || cfg?.second)
        this.initialValue.set(val)
        this.value.set(val)
        this.isLoading.set(false)
      })
    } catch (error) {
      runInAction(() => {
        this.error.set(`Failed to load root folder: ${extractRpcErrorMessage(error)}`)
        this.isLoading.set(false)
      })
      log.error("Failed to load root folder:", error)
    }
  }

  async save() {
    this.isSaving.set(true)
    this.error.set(null)

    try {
      await withGlobalLoader(this.context, this.updateProjectRootFolder(this.projectKey, this.repository, this.value.get()))
      runInAction(() => this.initialValue.set(this.value.get()))
    } catch (error) {
      runInAction(() => this.error.set(`Failed to save root folder: ${extractRpcErrorMessage(error)}`))
      log.error("Failed to save root folder:", error)
      throw error
    } finally {
      runInAction(() => this.isSaving.set(false))
    }
  }

  private async updateProjectRootFolder(projectKey: ProjectKey, repository: string, rootFolder: string) {
    await this.context.queries.apiClient.updateProjectRootFolder(projectKey, repository, rootFolder)
    void this.context.queryClient.invalidateQueries({ queryKey: projectRuntimeConfigQueryKey(projectKey, repository) })
  }

  discard = () => {
    runInAction(() => this.value.set(this.initialValue.get()))
  }
}
